import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import Field from '../components/Field.jsx'
import FormAlert from '../components/FormAlert.jsx'
import ThemeToggle from '../components/ThemeToggle.jsx'
import { useAuth } from '../context/AuthContext.jsx'
import { ApiError } from '../lib/api.js'

export default function Login() {
  const { login, loginWithCode, loginWithRecoveryCode } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const [form, setForm] = useState({ identifier: '', password: '' })
  const [alert, setAlert] = useState(null)
  const [busy, setBusy] = useState(false)
  // The second step, for an account with two-factor on (1B-4a). The challenge
  // lives here and nowhere else — not a cookie, not localStorage — and it is
  // gone the moment this component unmounts.
  const [challenge, setChallenge] = useState(null)
  const [code, setCode] = useState('')
  const [usingRecovery, setUsingRecovery] = useState(false)
  // A reset revokes every other session, so `ResetPassword` sends the person
  // here and this is the only place that can confirm it landed. Separate
  // state from `alert`, which the two-factor branch shares — and cleared in
  // both submit handlers, because an account with 2FA passes through
  // `onSubmitCode` while the notice is still up.
  const [reset, setReset] = useState(Boolean(location.state?.reset))

  useEffect(() => {
    document.title = 'Sign in — Sonologue'
  }, [])

  const set = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }))

  function backToPassword(message) {
    setChallenge(null)
    setCode('')
    setUsingRecovery(false)
    setForm((prev) => ({ ...prev, password: '' }))
    setAlert(message)
  }

  async function onSubmit(event) {
    event.preventDefault()
    setAlert(null)
    setReset(false)
    setBusy(true)
    try {
      await login(form)
      navigate(location.state?.from || '/', { replace: true })
    } catch (error) {
      // `totp-required` is not a failure: the password was right and the
      // account has a second factor. Branch on the code before anything
      // treats the problem body as a user.
      if (error instanceof ApiError && error.code === 'totp-required') {
        setChallenge(error.problem.challenge)
      } else {
        // Whatever else went wrong, the message is the API's — it is
        // deliberately the same for a wrong password and an unknown account
        // (PLAN §17.2).
        setAlert(error instanceof ApiError ? error.message : 'Something went wrong. Try again.')
      }
    } finally {
      setBusy(false)
    }
  }

  async function onSubmitCode(event) {
    event.preventDefault()
    setAlert(null)
    setReset(false)
    setBusy(true)
    try {
      if (usingRecovery) {
        await loginWithRecoveryCode({ challenge, recovery_code: code })
      } else {
        await loginWithCode({ challenge, code })
      }
      navigate(location.state?.from || '/', { replace: true })
    } catch (error) {
      // An expired or burned challenge is not something to retry a code
      // against — it means starting again with the password, so the screen
      // says so and goes back rather than leaving a form that cannot succeed.
      if (error instanceof ApiError && error.code === 'totp-challenge-expired') {
        backToPassword(error.message)
      } else {
        setAlert(error instanceof ApiError ? error.message : 'Something went wrong. Try again.')
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="auth-shell">
      <div className="absolute top-4 right-4">
        <ThemeToggle />
      </div>
      <div className="card card-pad auth-card fade-up">
        {challenge ? (
          <>
            <h1 className="auth-heading">Two-factor</h1>
            <p className="auth-sub">
              {usingRecovery
                ? 'Enter one of the recovery codes you saved.'
                : 'Enter the six-digit code from your authenticator app.'}
            </p>

            <FormAlert>{alert}</FormAlert>

            <form onSubmit={onSubmitCode} noValidate>
              {usingRecovery ? (
                <Field
                  label="Recovery code"
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                  hint="Using one signs you in and turns two-factor off, so you can set it up again."
                  autoComplete="one-time-code"
                  autoFocus
                  required
                />
              ) : (
                <Field
                  label="Six-digit code"
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  required
                />
              )}
              <button type="submit" className="btn btn-primary btn-block" disabled={busy}>
                {busy ? 'Checking…' : 'Sign in'}
              </button>
            </form>

            <p className="auth-alt">
              <button
                type="button"
                className="link-btn"
                onClick={() => {
                  setCode('')
                  setAlert(null)
                  setUsingRecovery((prev) => !prev)
                }}
              >
                {usingRecovery ? 'Use your authenticator app' : 'Use a recovery code'}
              </button>
            </p>
            <p className="auth-alt">
              <button type="button" className="link-btn" onClick={() => backToPassword(null)}>
                Start again
              </button>
            </p>
          </>
        ) : (
          <>
            <h1 className="auth-heading">Sign in</h1>
            <p className="auth-sub">Welcome back.</p>

            <FormAlert tone="positive">
              {reset
                ? 'Your password is set. Sign in with it — every other device has been signed out.'
                : null}
            </FormAlert>
            <FormAlert>{alert}</FormAlert>

            <form onSubmit={onSubmit} noValidate>
              <Field
                label="Email or username"
                value={form.identifier}
                onChange={set('identifier')}
                autoComplete="username"
                required
              />
              <Field
                label="Password"
                type="password"
                value={form.password}
                onChange={set('password')}
                autoComplete="current-password"
                required
              />
              <button type="submit" className="btn btn-primary btn-block" disabled={busy}>
                {busy ? 'Signing in…' : 'Sign in'}
              </button>
            </form>

            <p className="auth-alt">
              <Link to="/forgot">Forgot your password?</Link>
            </p>
            <p className="auth-alt">
              Have an invite code? <Link to="/welcome">Create an account</Link>
            </p>
          </>
        )}
      </div>
    </div>
  )
}
